import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui";
import { Input } from "@/components/ui/Input";
import { FormDatePicker } from "@/components/ui/FormDatePicker";
import { Select } from "@/components/ui/Select";
import { CustomerCombobox } from "@/components/ui/CustomerCombobox";
import { ResolveTokenDetails } from "@/components/contracts/ResolveTokenDetails";
import { useProducts, useProductWithUsagePlans } from "@/hooks/products";
import { usePricingPolicies } from "@/hooks/contracts";
import { useCustomers } from "@/hooks/customers";
import { CREATE_CONTRACT_CURRENCIES } from "@/constants/contracts";
import { isLicensedProduct } from "@/utils/contractHelpers";
import { DateInputValueFormat, formatDateInput } from "@/utils/date";
import { DEFAULT_RESOLVE_TOKEN_EXPIRY_HOURS, MAX_RESOLVE_TOKEN_EXPIRY_HOURS, MIN_RESOLVE_TOKEN_EXPIRY_HOURS, resolveTokenExpiryHoursSchema } from "@/utils/resolveTokenExpiry";
const LICENSE_FEATURE_KEYS = [
    'ipsec',
    'dqkd',
    'blast',
    'eaas'
];
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const RFC3339_UTC_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
function getDateOnly(value) {
    return value.slice(0, 10);
}
const optionalSelectValueSchema = z.preprocess((value)=>{
    if (typeof value !== 'string') return value;
    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : undefined;
}, z.string().optional());
const licenseTermsSchema = z.object({
    max_devices: z.number().int().min(1, 'Must have at least 1 device'),
    features: z.object({
        ipsec: z.number().int().min(0, 'Must be 0 or more'),
        dqkd: z.number().int().min(0, 'Must be 0 or more'),
        blast: z.number().int().min(0, 'Must be 0 or more'),
        eaas: z.number().int().min(0, 'Must be 0 or more')
    })
}).superRefine((data, ctx)=>{
    for (const key of LICENSE_FEATURE_KEYS){
        if (data.features[key] > data.max_devices) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Cannot exceed max devices (${data.max_devices})`,
                path: [
                    'features',
                    key
                ]
            });
        }
    }
});
// Form validation schema
const contractFormSchema = z.object({
    customerSelection: z.enum([
        'existing',
        'new'
    ]).default('new'),
    customerId: z.string().optional(),
    customerName: z.string().optional(),
    customerEmail: z.string().optional(),
    productCode: z.string().min(1, 'Product is required'),
    usagePlanId: optionalSelectValueSchema,
    pricingPolicyId: optionalSelectValueSchema,
    currency: z.enum([
        CREATE_CONTRACT_CURRENCIES.USD,
        CREATE_CONTRACT_CURRENCIES.EUR
    ]).default(CREATE_CONTRACT_CURRENCIES.USD),
    contractType: z.enum([
        'DIRECT',
        'TRIAL'
    ]).default('DIRECT'),
    contractName: z.string().trim().min(1, 'Contract name is required'),
    sla: z.string().optional(),
    contractExpiryDate: z.string().regex(RFC3339_UTC_PATTERN, 'Must be RFC 3339 UTC format'),
    billingStartDate: z.union([
        z.string().regex(DATE_ONLY_PATTERN, 'Must be YYYY-MM-DD format'),
        z.literal('')
    ]).optional(),
    licenseExpiryDateOverride: z.union([
        z.string().regex(DATE_ONLY_PATTERN, 'Must be YYYY-MM-DD format'),
        z.literal('')
    ]).optional(),
    resolveTokenExpiryHours: resolveTokenExpiryHoursSchema.default(DEFAULT_RESOLVE_TOKEN_EXPIRY_HOURS),
    // License terms — only required for LICENSED_CLUSTER products (enforced by refine below)
    isLicensedProduct: z.boolean().default(false),
    license_terms: licenseTermsSchema.optional()
}).refine((data)=>{
    // Usage plan required for non-licensed products
    if (!data.isLicensedProduct && (!data.usagePlanId || data.usagePlanId.trim().length === 0)) {
        return false;
    }
    return true;
}, {
    message: 'Usage plan is required',
    path: [
        'usagePlanId'
    ]
}).refine((data)=>{
    // Pricing policy required for non-licensed products
    if (!data.isLicensedProduct && (!data.pricingPolicyId || data.pricingPolicyId.trim().length === 0)) {
        return false;
    }
    return true;
}, {
    message: 'Pricing policy is required',
    path: [
        'pricingPolicyId'
    ]
}).refine((data)=>{
    if (data.customerSelection === 'existing') {
        return !!data.customerId;
    } else {
        return !!data.customerName && !!data.customerEmail;
    }
}, {
    message: 'Either select an existing customer or provide new customer details',
    path: [
        'customerSelection'
    ]
}).refine((data)=>{
    if (data.customerSelection === 'new') {
        return data.customerName && data.customerName.trim().length > 0;
    }
    return true;
}, {
    message: 'Customer name is required',
    path: [
        'customerName'
    ]
}).refine((data)=>{
    if (data.customerSelection === 'new') {
        return data.customerEmail && data.customerEmail.trim().length > 0;
    }
    return true;
}, {
    message: 'Customer email is required',
    path: [
        'customerEmail'
    ]
}).refine((data)=>{
    // Validate email format only when creating new customer and email is provided
    if (data.customerSelection === 'new' && data.customerEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(data.customerEmail);
    }
    return true;
}, {
    message: 'Invalid email format',
    path: [
        'customerEmail'
    ]
}).refine((data)=>{
    if (data.isLicensedProduct || !data.billingStartDate) {
        return true;
    }
    return data.billingStartDate <= getDateOnly(data.contractExpiryDate);
}, {
    message: 'Billing start date must be on or before contract expiry date',
    path: [
        'billingStartDate'
    ]
}).refine((data)=>{
    if (!data.licenseExpiryDateOverride) {
        return true;
    }
    return data.licenseExpiryDateOverride >= getDateOnly(data.contractExpiryDate);
}, {
    message: 'License expiry override must be on or after contract expiry date',
    path: [
        'licenseExpiryDateOverride'
    ]
}).refine((data)=>{
    // License terms required for licensed products
    if (data.isLicensedProduct && !data.license_terms) {
        return false;
    }
    return true;
}, {
    message: 'License terms are required for licensed products',
    path: [
        'license_terms'
    ]
});
export const CreateContractModal = ({ isOpen, onClose, onSubmit, isLoading })=>{
    const [createdContract, setCreatedContract] = useState(null);
    const [selectedProductCode, setSelectedProductCode] = useState('');
    const { data: productsData, isLoading: productsLoading } = useProducts();
    const { data: productDetails, isLoading: usagePlansLoading } = useProductWithUsagePlans(selectedProductCode);
    const { data: pricingData, isLoading: pricingLoading } = usePricingPolicies();
    const { data: customersData, isLoading: customersLoading } = useCustomers();
    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm({
        resolver: zodResolver(contractFormSchema),
        defaultValues: {
            customerSelection: 'new',
            contractType: 'DIRECT',
            currency: CREATE_CONTRACT_CURRENCIES.USD,
            resolveTokenExpiryHours: DEFAULT_RESOLVE_TOKEN_EXPIRY_HOURS,
            contractExpiryDate: getDefaultExpiryDate()
        }
    });
    const productCode = watch('productCode');
    const customerSelection = watch('customerSelection');
    const watchedCustomerId = watch('customerId');
    // Map customer data for the combobox
    const customerOptions = useMemo(()=>{
        if (!customersData?.organizations) return [];
        return customersData.organizations.map((org)=>({
                id: org.customerId,
                name: org.name,
                email: org.email
            }));
    }, [
        customersData
    ]);
    // Detect if selected product is a licensed product
    const isLicensed = useMemo(()=>{
        if (!productDetails?.product) return false;
        return isLicensedProduct(productDetails.product);
    }, [
        productDetails
    ]);
    // Sync the isLicensedProduct flag and default license terms when product type changes
    useEffect(()=>{
        setValue('isLicensedProduct', isLicensed);
        if (isLicensed) {
            setValue('license_terms', {
                max_devices: 1,
                features: {
                    ipsec: 0,
                    dqkd: 0,
                    blast: 0,
                    eaas: 0
                }
            });
            // Licensed products don't use usage plans or pricing policies
            setValue('usagePlanId', undefined, {
                shouldValidate: true
            });
            setValue('pricingPolicyId', undefined, {
                shouldValidate: true
            });
            setValue('billingStartDate', undefined, {
                shouldValidate: true
            });
        } else {
            setValue('license_terms', undefined);
            setValue('licenseExpiryDateOverride', undefined, {
                shouldValidate: true
            });
        }
    }, [
        isLicensed,
        setValue
    ]);
    useEffect(()=>{
        if (productCode && productCode !== selectedProductCode) {
            setSelectedProductCode(productCode);
            // Reset usage plan when product changes
            setValue('usagePlanId', undefined);
        }
    }, [
        productCode,
        selectedProductCode,
        setValue
    ]);
    useEffect(()=>{
        // Clear customer fields when switching selection type
        if (customerSelection === 'existing') {
            setValue('customerName', '');
            setValue('customerEmail', '');
        } else {
            setValue('customerId', '');
        }
    }, [
        customerSelection,
        setValue
    ]);
    const handleFormSubmit = async (data)=>{
        try {
            // Transform data based on customer selection
            const { customerSelection: _customerSelection, isLicensedProduct: _isLicensed, usagePlanId, pricingPolicyId, billingStartDate, licenseExpiryDateOverride, contractName, ...restData } = data;
            const resolvedContractName = contractName.trim();
            const submitData = {
                ...restData,
                contractName: resolvedContractName,
                ...data.customerSelection === 'existing' && data.customerId ? {
                    customerId: data.customerId
                } : {},
                ...!_isLicensed && usagePlanId ? {
                    usagePlanId
                } : {},
                ...!_isLicensed && pricingPolicyId ? {
                    pricingPolicyId
                } : {},
                ...!_isLicensed && billingStartDate ? {
                    billingStartDate
                } : {},
                ..._isLicensed && licenseExpiryDateOverride ? {
                    licenseExpiryDateOverride
                } : {},
                // Only include license_terms for licensed products
                license_terms: _isLicensed ? data.license_terms : undefined
            };
            if (data.customerSelection === 'existing') {
                delete submitData.customerName;
                delete submitData.customerEmail;
                delete submitData.resolveTokenExpiryHours;
            } else {
                delete submitData.customerId;
            }
            const result = await onSubmit(submitData);
            setCreatedContract(result);
            reset();
        } catch (error) {
        // Error handled by parent component
        }
    };
    const handleClose = ()=>{
        setCreatedContract(null);
        setSelectedProductCode('');
        reset();
        onClose();
    };
    function getDefaultExpiryDate() {
        const date = new Date();
        date.setDate(date.getDate() + 14); // 2 weeks from now
        return formatDateInput(date, DateInputValueFormat.Rfc3339EndOfDay);
    }
    if (!isOpen) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50",
        children: /*#__PURE__*/ _jsx("div", {
            className: "relative top-1/2 transform -translate-y-1/2 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white",
            children: /*#__PURE__*/ _jsx("div", {
                children: createdContract ? // Success state - show the created contract
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: "Contract Created Successfully"
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    onClick: handleClose,
                                    className: "text-gray-400 hover:text-gray-600",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "sr-only",
                                            children: "Close"
                                        }),
                                        /*#__PURE__*/ _jsx("svg", {
                                            className: "h-6 w-6",
                                            fill: "none",
                                            viewBox: "0 0 24 24",
                                            stroke: "currentColor",
                                            children: /*#__PURE__*/ _jsx("path", {
                                                strokeLinecap: "round",
                                                strokeLinejoin: "round",
                                                strokeWidth: 2,
                                                d: "M6 18L18 6M6 6l12 12"
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        !createdContract.resolveToken && /*#__PURE__*/ _jsx("div", {
                            className: "mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-blue-800",
                                children: "No signup link was generated because this contract was created for an existing customer."
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-gray-700 mb-1",
                                            children: "Customer"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "text-sm text-gray-900",
                                            children: [
                                                createdContract.customer.name,
                                                " (",
                                                createdContract.customer.email,
                                                ")"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "text-xs text-gray-500 mt-1",
                                            children: [
                                                "Customer ID: ",
                                                createdContract.customer.customerId
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-gray-700 mb-1",
                                            children: "Contract"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-sm text-gray-900",
                                            children: createdContract.contract.contractName
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "text-xs text-gray-500",
                                            children: [
                                                "Expires: ",
                                                new Date(createdContract.contract.expiryDate).toLocaleDateString()
                                            ]
                                        })
                                    ]
                                }),
                                createdContract.resolveToken && /*#__PURE__*/ _jsx(ResolveTokenDetails, {
                                    resolveToken: createdContract.resolveToken
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-6 flex justify-end",
                            children: /*#__PURE__*/ _jsx(Button, {
                                variant: "simple",
                                color: "navy-90",
                                onClick: handleClose,
                                children: "Close"
                            })
                        })
                    ]
                }) : // Form state - create new contract
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: "Create New Contract"
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    onClick: handleClose,
                                    className: "text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed",
                                    disabled: isLoading,
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "sr-only",
                                            children: "Close"
                                        }),
                                        /*#__PURE__*/ _jsx("svg", {
                                            className: "h-6 w-6",
                                            fill: "none",
                                            viewBox: "0 0 24 24",
                                            stroke: "currentColor",
                                            children: /*#__PURE__*/ _jsx("path", {
                                                strokeLinecap: "round",
                                                strokeLinejoin: "round",
                                                strokeWidth: 2,
                                                d: "M6 18L18 6M6 6l12 12"
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("form", {
                            onSubmit: handleSubmit(handleFormSubmit),
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 max-h-[60vh] overflow-y-auto pr-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "border-b pb-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("h4", {
                                                    className: "text-sm font-semibold text-gray-900 mb-3",
                                                    children: "Customer Information"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "space-y-3",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs(Select, {
                                                            label: "Customer Selection *",
                                                            id: "customerSelection",
                                                            ...register('customerSelection'),
                                                            children: [
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "new",
                                                                    children: "Create New Customer"
                                                                }),
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "existing",
                                                                    children: "Select Existing Customer"
                                                                })
                                                            ]
                                                        }),
                                                        errors.customerSelection && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-red-600",
                                                            children: errors.customerSelection.message
                                                        }),
                                                        customerSelection === 'existing' ? /*#__PURE__*/ _jsx(CustomerCombobox, {
                                                            label: "Existing Customer *",
                                                            customers: customerOptions,
                                                            value: watchedCustomerId || null,
                                                            onChange: (id)=>setValue('customerId', id ?? '', {
                                                                    shouldValidate: true
                                                                }),
                                                            disabled: customersLoading || !customersData?.organizations,
                                                            placeholder: customersLoading ? 'Loading customers...' : 'Search customers...',
                                                            errors: errors.customerId
                                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Input, {
                                                                    label: "Customer Name *",
                                                                    id: "customerName",
                                                                    ...register('customerName'),
                                                                    placeholder: "Acme Corporation",
                                                                    errors: errors.customerName
                                                                }),
                                                                /*#__PURE__*/ _jsx(Input, {
                                                                    label: "Customer Email *",
                                                                    id: "customerEmail",
                                                                    type: "email",
                                                                    ...register('customerEmail'),
                                                                    placeholder: "admin@acme.com",
                                                                    errors: errors.customerEmail
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "border-b pb-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("h4", {
                                                    className: "text-sm font-semibold text-gray-900 mb-3",
                                                    children: "Product & Plan"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "space-y-3",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs(Select, {
                                                            label: "Product *",
                                                            id: "productCode",
                                                            ...register('productCode'),
                                                            disabled: productsLoading || !productsData?.products,
                                                            children: [
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "",
                                                                    children: productsLoading ? 'Loading products...' : 'Select a product'
                                                                }),
                                                                productsData?.products?.map((product)=>/*#__PURE__*/ _jsx("option", {
                                                                        value: product.ProductCode,
                                                                        children: product.ProductName
                                                                    }, product.ProductCode))
                                                            ]
                                                        }),
                                                        errors.productCode && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-red-600",
                                                            children: errors.productCode.message
                                                        }),
                                                        !isLicensed && /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsxs(Select, {
                                                                    label: "Usage Plan *",
                                                                    id: "usagePlanId",
                                                                    ...register('usagePlanId'),
                                                                    disabled: !selectedProductCode || usagePlansLoading || !productDetails?.usagePlans?.length,
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("option", {
                                                                            value: "",
                                                                            children: !selectedProductCode ? 'Select a product first' : usagePlansLoading ? 'Loading usage plans...' : 'Select a usage plan'
                                                                        }),
                                                                        productDetails?.usagePlans?.map((plan)=>/*#__PURE__*/ _jsxs("option", {
                                                                                value: plan.UsagePlanId,
                                                                                children: [
                                                                                    plan.Name,
                                                                                    " - ",
                                                                                    plan.RateLimit,
                                                                                    " req/sec, ",
                                                                                    plan.QuotaValue,
                                                                                    " ",
                                                                                    plan.QuotaUnits
                                                                                ]
                                                                            }, plan.UsagePlanId))
                                                                    ]
                                                                }),
                                                                errors.usagePlanId && /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-sm text-red-600",
                                                                    children: errors.usagePlanId.message
                                                                }),
                                                                /*#__PURE__*/ _jsxs(Select, {
                                                                    label: "Pricing Policy *",
                                                                    id: "pricingPolicyId",
                                                                    ...register('pricingPolicyId'),
                                                                    disabled: pricingLoading || !pricingData?.pricingPolicies,
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("option", {
                                                                            value: "",
                                                                            children: pricingLoading ? 'Loading pricing policies...' : 'Select a pricing policy'
                                                                        }),
                                                                        pricingData?.pricingPolicies?.map((policy)=>/*#__PURE__*/ _jsxs("option", {
                                                                                value: policy.PricingPolicyId,
                                                                                children: [
                                                                                    policy.PricingPolicyId,
                                                                                    " - ",
                                                                                    policy.Model,
                                                                                    " (",
                                                                                    policy.Currency,
                                                                                    ")"
                                                                                ]
                                                                            }, policy.PricingPolicyId))
                                                                    ]
                                                                }),
                                                                errors.pricingPolicyId && /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-sm text-red-600",
                                                                    children: errors.pricingPolicyId.message
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs(Select, {
                                                            label: "Currency *",
                                                            id: "currency",
                                                            ...register('currency'),
                                                            children: [
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: CREATE_CONTRACT_CURRENCIES.USD,
                                                                    children: "USD ($)"
                                                                }),
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: CREATE_CONTRACT_CURRENCIES.EUR,
                                                                    children: "EUR (€)"
                                                                })
                                                            ]
                                                        }),
                                                        errors.currency && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-red-600",
                                                            children: errors.currency.message
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        isLicensed && /*#__PURE__*/ _jsxs("div", {
                                            className: "border-b pb-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("h4", {
                                                    className: "text-sm font-semibold text-gray-900 mb-1",
                                                    children: "License Terms"
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-xs text-gray-500 mb-3",
                                                    children: "Configure device limits and feature seat allocations for this licensed product."
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "space-y-3",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Input, {
                                                            label: "Max Devices *",
                                                            id: "license_terms.max_devices",
                                                            type: "number",
                                                            ...register('license_terms.max_devices', {
                                                                valueAsNumber: true
                                                            }),
                                                            min: 1,
                                                            placeholder: "1",
                                                            errors: errors.license_terms?.max_devices
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-xs text-gray-500",
                                                            children: "Maximum number of devices allowed in the cluster. Feature seat counts below cannot exceed this value."
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "grid grid-cols-2 gap-3",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Input, {
                                                                    label: "IPsec Seats",
                                                                    id: "license_terms.features.ipsec",
                                                                    type: "number",
                                                                    ...register('license_terms.features.ipsec', {
                                                                        valueAsNumber: true
                                                                    }),
                                                                    min: 0,
                                                                    placeholder: "0",
                                                                    errors: errors.license_terms?.features?.ipsec
                                                                }),
                                                                /*#__PURE__*/ _jsx(Input, {
                                                                    label: "DQKD Seats",
                                                                    id: "license_terms.features.dqkd",
                                                                    type: "number",
                                                                    ...register('license_terms.features.dqkd', {
                                                                        valueAsNumber: true
                                                                    }),
                                                                    min: 0,
                                                                    placeholder: "0",
                                                                    errors: errors.license_terms?.features?.dqkd
                                                                }),
                                                                /*#__PURE__*/ _jsx(Input, {
                                                                    label: "Blast Seats",
                                                                    id: "license_terms.features.blast",
                                                                    type: "number",
                                                                    ...register('license_terms.features.blast', {
                                                                        valueAsNumber: true
                                                                    }),
                                                                    min: 0,
                                                                    placeholder: "0",
                                                                    errors: errors.license_terms?.features?.blast
                                                                }),
                                                                /*#__PURE__*/ _jsx(Input, {
                                                                    label: "EaaS Seats",
                                                                    id: "license_terms.features.eaas",
                                                                    type: "number",
                                                                    ...register('license_terms.features.eaas', {
                                                                        valueAsNumber: true
                                                                    }),
                                                                    min: 0,
                                                                    placeholder: "0",
                                                                    errors: errors.license_terms?.features?.eaas
                                                                })
                                                            ]
                                                        }),
                                                        errors.license_terms && !errors.license_terms.max_devices && !errors.license_terms.features && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-red-600",
                                                            children: errors.license_terms.message || errors.license_terms.root?.message
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "border-b pb-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("h4", {
                                                    className: "text-sm font-semibold text-gray-900 mb-3",
                                                    children: "Contract Details"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "space-y-3",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs(Select, {
                                                            label: "Contract Type *",
                                                            id: "contractType",
                                                            ...register('contractType'),
                                                            children: [
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "DIRECT",
                                                                    children: "Direct Contract"
                                                                }),
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "TRIAL",
                                                                    children: "Trial Contract"
                                                                })
                                                            ]
                                                        }),
                                                        errors.contractType && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-red-600",
                                                            children: errors.contractType.message
                                                        }),
                                                        /*#__PURE__*/ _jsx(Input, {
                                                            label: "Contract Name *",
                                                            id: "contractName",
                                                            ...register('contractName'),
                                                            placeholder: "Enter contract name",
                                                            errors: errors.contractName
                                                        }),
                                                        /*#__PURE__*/ _jsx(Input, {
                                                            label: "SLA (optional)",
                                                            id: "sla",
                                                            ...register('sla'),
                                                            placeholder: "Standard SLA - 99.9% uptime"
                                                        }),
                                                        /*#__PURE__*/ _jsx(FormDatePicker, {
                                                            label: "Contract Expiry Date *",
                                                            name: "contractExpiryDate",
                                                            control: control,
                                                            placeholder: "Select contract expiry date",
                                                            errors: errors.contractExpiryDate,
                                                            valueFormat: DateInputValueFormat.Rfc3339EndOfDay
                                                        }),
                                                        isLicensed ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(FormDatePicker, {
                                                                    label: "License Expiry Override (optional)",
                                                                    name: "licenseExpiryDateOverride",
                                                                    control: control,
                                                                    placeholder: "Defaults to contract expiry + 30 days",
                                                                    errors: errors.licenseExpiryDateOverride
                                                                }),
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: "If left blank, the license expires 30 days after the contract expiry date."
                                                                })
                                                            ]
                                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(FormDatePicker, {
                                                                    label: "Billing Start Date (optional)",
                                                                    name: "billingStartDate",
                                                                    control: control,
                                                                    placeholder: "Defaults to contract creation date",
                                                                    errors: errors.billingStartDate
                                                                }),
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: "Use this for migrations or delayed billing. If left blank, billing starts when the contract is created."
                                                                })
                                                            ]
                                                        }),
                                                        customerSelection === 'new' && /*#__PURE__*/ _jsxs(_Fragment, {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Input, {
                                                                    label: "Resolve Token Expiry (hours) *",
                                                                    id: "resolveTokenExpiryHours",
                                                                    type: "number",
                                                                    ...register('resolveTokenExpiryHours', {
                                                                        valueAsNumber: true
                                                                    }),
                                                                    min: MIN_RESOLVE_TOKEN_EXPIRY_HOURS,
                                                                    max: MAX_RESOLVE_TOKEN_EXPIRY_HOURS,
                                                                    step: 1,
                                                                    errors: errors.resolveTokenExpiryHours
                                                                }),
                                                                /*#__PURE__*/ _jsxs("p", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: [
                                                                        "Token expires between ",
                                                                        MIN_RESOLVE_TOKEN_EXPIRY_HOURS,
                                                                        "-",
                                                                        MAX_RESOLVE_TOKEN_EXPIRY_HOURS,
                                                                        " hours (1 hour to 7 days)"
                                                                    ]
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex justify-end space-x-3 pt-4 mt-4 border-t",
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            type: "button",
                                            variant: "simple",
                                            buttonStyle: "flat",
                                            onClick: handleClose,
                                            disabled: isLoading,
                                            children: "Cancel"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            type: "submit",
                                            variant: "simple",
                                            color: "navy-90",
                                            disabled: isLoading,
                                            children: isLoading ? 'Creating...' : 'Create Contract'
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        })
    });
};
